import { AxiosResponse } from 'axios'
import {
  call,
  cancel,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import {
  deleteEntityAction,
  getAllEntitiesAction,
  getAllEntitiesAgentAction,
  getAllEntitiesPaginatedAction,
  getBookmarkedEntitesAction,
  toggleEntityAction,
  updateAction,
  updateBookmarkEntities,
  updateEntities,
  updateEntitiesByName,
  updateEntitiesMapById,
  updateEntitiesNameMapByTableName,
  updateGetAllEntitiesByPage,
  updateLoader,
} from '../slices/entity.slice'
import {
  getAllEntities,
  deleteEntityByIdApi,
  toggleEntityByIdApi,
  getBookmarkedEntites,
  postBookmarkedEntites,
  fetchFilteredEntityList,
} from '../apis/entity.api'
import { SagaIterator, Task } from 'redux-saga'
import { PayloadAction } from '@reduxjs/toolkit'
import { addToaster, hideLoader, showLoader } from '../slices'
import { v4 as uuid } from 'uuid'
import { NAMESPACE } from '../utils/strings'
import { IEntityType } from '../types'
import { DefaultBookmarkedEntitiesForAgentPortal } from '../utils/constants'
import { TableFilterApiBody } from '../types/tableColumn.types'
import { filterNonAgentVisibleEntitiesFromBookmark } from '../utils/entities.utils'
import { getTenantIdFromWindow } from '../utils/portalTenantQuery'

function* fetchAllEntities(): SagaIterator {
  try {
    yield put(showLoader())
    const response = yield call(getAllEntities)
    yield put(updateEntities(response?.data))
  } catch (err) {
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

function* fetchAllEntitiesAgent(): SagaIterator {
  try {
    yield put(showLoader())
    const response: AxiosResponse<IEntityType[]> | undefined = yield call(
      getAllEntities
    )
    const entitiesByName: Record<string, IEntityType> = {}
    const entitiesMapById: Record<string, string> = {}
    const entitiesNameMapByTableName: Record<string, string> = {}
    response?.data?.forEach((entity) => {
      entitiesByName[entity.name] = entity
      entitiesMapById[entity.id] = entity.name
      if (entity.table_name) {
        entitiesNameMapByTableName[entity.table_name] = entity.name
      }
    })

    yield put(updateEntitiesByName(entitiesByName))
    yield put(updateEntitiesMapById(entitiesMapById))
    yield put(updateEntitiesNameMapByTableName(entitiesNameMapByTableName))
    // This is a temporary solution, please do not remove it
    const tempEntities = response?.data?.filter((entity) => {
      return entity?.agent_visibility
    })
    const taskEntity = tempEntities?.filter((ele) => ele?.name === 'task') ?? []
    const nonTaskEntity =
      tempEntities?.filter((ele) => ele?.name !== 'task') ?? []
    yield put(updateEntities([...nonTaskEntity, ...taskEntity]))
    yield put(getBookmarkedEntitesAction())
  } catch (err) {
    yield put(updateEntities([])) // when we change client & API fails
    console.error(err)
  } finally {
    yield put(hideLoader())
  }
}

function* fetchAllEntitiesPaginated(
  action: PayloadAction<{
    page: number
    size: string
    filter: TableFilterApiBody
    sort?: string
    includeSourceType?: string
  }>
): SagaIterator {
  try {
    yield put(updateLoader(true))
    const response = yield call(fetchFilteredEntityList, {
      ...action.payload,
      sort: action?.payload?.sort,
    })
    yield put(updateGetAllEntitiesByPage(response?.data))
  } catch (_err) {
    console.log(_err)
  } finally {
    yield put(updateLoader(false))
  }
}

function* fetchBookmarkedEntities(): SagaIterator {
  try {
    yield put(showLoader())
    const response = yield call(getBookmarkedEntites)
    const entities = yield select((state) => state.entity.entities)
    const agentVisibleBookmarkedEntities =
      filterNonAgentVisibleEntitiesFromBookmark(response?.data, entities)
    if (agentVisibleBookmarkedEntities.length === 0) {
      const entitiesToPost = entities.filter((entity: IEntityType) =>
        DefaultBookmarkedEntitiesForAgentPortal.includes(
          entity?.name?.toLowerCase()
        )
      )
      // Discussed to remove the default first entity
      // if (entitiesToPost.length === 0 && entities.length > 0) {
      //   entitiesToPost.push(entities[0])
      // }
      yield call(postBookmarkedEntites, entitiesToPost)
      const refetchResponse = yield call(getBookmarkedEntites)
      yield put(
        updateBookmarkEntities(
          filterNonAgentVisibleEntitiesFromBookmark(
            refetchResponse?.data,
            entities
          )
        )
      )
    } else {
      yield put(updateBookmarkEntities(agentVisibleBookmarkedEntities))
    }
  } catch (err) {
    yield put(updateBookmarkEntities([])) // If we change client & API fails
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

function* deleteEntity(
  action: PayloadAction<{ entityId: number; page: number; size: string }>
): SagaIterator {
  try {
    yield call(deleteEntityByIdApi, action?.payload?.entityId)
    yield put(getAllEntitiesAction())
    yield put(
      getAllEntitiesPaginatedAction({
        page: action?.payload?.page,
        size: action?.payload?.size,
        filter: {
          fields: null,
          search_criteria: [],
        },
      })
    )
    yield put(
      addToaster({
        id: uuid(),
        message: NAMESPACE.DELETE_SUCCESS,
        type: 'success',
      })
    )
  } catch (err) {
    yield put(
      addToaster({
        id: uuid(),
        message: NAMESPACE.SOMETHING_WENT_WRONG,
        type: 'error',
      })
    )
  }
}

function* toggleEntity(
  action: PayloadAction<{
    entityId: number
    status: boolean
    page: number
    size: string
    includeSourceTypes?: string
    filter?: TableFilterApiBody
    sort?: string
  }>
): SagaIterator {
  try {
    yield call(
      toggleEntityByIdApi,
      action?.payload?.entityId,
      action?.payload?.status
    )
    yield put(getAllEntitiesAction())
    const response = yield call(fetchFilteredEntityList, action.payload)
    yield put(updateGetAllEntitiesByPage(response?.data))
    yield put(
      addToaster({
        id: uuid(),
        message: action?.payload?.status
          ? NAMESPACE.ENABLED_SUCCESS
          : NAMESPACE.DISABLED_SUCCESS,
        type: 'success',
      })
    )
  } catch (err) {
    yield put(
      addToaster({
        id: uuid(),
        message: NAMESPACE.SOMETHING_WENT_WRONG,
        type: 'error',
      })
    )
  }
}

function* updateBookmarkedEntity(
  action: PayloadAction<
    { id: string; name: string; singular_label: string; plural_label: string }[]
  >
): SagaIterator {
  try {
    yield put(showLoader())
    yield call(postBookmarkedEntites, action.payload)
    yield call(fetchBookmarkedEntities)
  } catch (err) {
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

export function* watchFetchAllEntities() {
  yield takeLatest(getAllEntitiesAction.type, fetchAllEntities)
}

export function* watchFetchAllEntitiesPaginated() {
  yield takeLatest(
    getAllEntitiesPaginatedAction.type,
    fetchAllEntitiesPaginated
  )
}

// Several components dispatch this together (e.g. PageLayout + Search on load).
// While a fetch for the same tenant is in flight, extra dispatches reuse it;
// a tenant switch cancels it and starts a new one (same as takeLatest).
export function* watchFetchAllEntitiesAgent(): SagaIterator {
  let task: Task | undefined
  let taskTenantId: number | null = null
  while (true) {
    yield take(getAllEntitiesAgentAction.type)
    const tenantId = getTenantIdFromWindow()
    if (task?.isRunning()) {
      if (tenantId === taskTenantId) continue
      yield cancel(task)
    }
    taskTenantId = tenantId
    task = yield fork(fetchAllEntitiesAgent)
  }
}

export function* watchFetchDeleteEntity() {
  yield takeLatest(deleteEntityAction.type, deleteEntity)
}

export function* watchToggleEntity() {
  yield takeLatest(toggleEntityAction.type, toggleEntity)
}

export function* watchBookmarkedEntities() {
  yield takeLatest(getBookmarkedEntitesAction.type, fetchBookmarkedEntities)
}

export function* watchUpdateBookmarkedEntites() {
  yield takeLatest(updateAction.type, updateBookmarkedEntity)
}
